import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectCursorPages } from './pagination';
import { api, ApiError, unwrap } from './client';
import type { components } from './schema';

export type ProjectSummary = components['schemas']['ProjectList'];
export type Project = components['schemas']['ProjectDetail'];
export type ProjectCreateBody = components['schemas']['ProjectCreateRequest'];
export type ProjectPatch = components['schemas']['PatchedProjectPatchRequest'];
export type Share = components['schemas']['Share'];
export type ShareRole = components['schemas']['ShareRoleEnum'];
export type GrantShareBody = components['schemas']['GrantShareRequest'];
export type AuditEvent = components['schemas']['AuditEvent'];
export type LifecycleAction = 'complete' | 'reopen' | 'archive' | 'restore';

/** Effective role as the API reports it: a membership or share role, or `staff`. */
export type EffectiveRole = 'staff' | 'owner' | 'manager' | 'contributor' | 'viewer' | 'uploader';

export const SHARE_ROLES: ShareRole[] = ['contributor', 'viewer', 'uploader'];

/** Mirrors projects.access on the backend; the UI hides what the API would refuse. */
export const canUpload = (r: string | undefined) => ['staff', 'owner', 'manager', 'contributor', 'uploader'].includes(r ?? '');
export const canShare = (r: string | undefined) => ['staff', 'owner', 'manager'].includes(r ?? '');
export const canViewContents = (r: string | undefined) => !!r && r !== 'uploader';
export const canCreateProject = (r: string | null | undefined, isStaff?: boolean) => !!isStaff || r === 'owner' || r === 'manager';

export interface ProjectFilters {
  organization?: string;
  includeArchived?: boolean;
}

export async function listProjects(f: ProjectFilters = {}): Promise<ProjectSummary[]> {
  return collectCursorPages(async (cursor) =>
    unwrap(
      await api.GET('/api/projects', {
        params: { query: { organization: f.organization, include_archived: f.includeArchived || undefined, cursor } },
      }),
    ),
  );
}
export async function getProject(id: string): Promise<Project> {
  return unwrap(await api.GET('/api/projects/{public_id}', { params: { path: { public_id: id } } }));
}
export async function createProject(body: ProjectCreateBody): Promise<Project> {
  return unwrap(await api.POST('/api/projects', { body }));
}
export async function updateProject(id: string, patch: ProjectPatch): Promise<Project> {
  return unwrap(await api.PATCH('/api/projects/{public_id}', { params: { path: { public_id: id } }, body: patch }));
}
export async function lifecycle(id: string, action: LifecycleAction, reason = ''): Promise<Project> {
  const params = { params: { path: { public_id: id } }, body: { reason } };
  switch (action) {
    case 'complete':
      return unwrap(await api.POST('/api/projects/{public_id}/complete', params));
    case 'reopen':
      return unwrap(await api.POST('/api/projects/{public_id}/reopen', params));
    case 'archive':
      return unwrap(await api.POST('/api/projects/{public_id}/archive', params));
    case 'restore':
      return unwrap(await api.POST('/api/projects/{public_id}/restore', params));
  }
}
export async function listShares(projectId: string): Promise<Share[]> {
  return unwrap(await api.GET('/api/projects/{public_id}/shares', { params: { path: { public_id: projectId } } }));
}
export async function grantShare(projectId: string, body: GrantShareBody): Promise<Share> {
  return unwrap(await api.POST('/api/projects/{public_id}/shares', { params: { path: { public_id: projectId } }, body }));
}
export async function revokeShare(projectId: string, shareId: string): Promise<void> {
  unwrap(
    await api.DELETE('/api/projects/{public_id}/shares/{share_id}', {
      params: { path: { public_id: projectId, share_id: shareId } },
    }),
  );
}
export async function listProjectAudit(projectId: string): Promise<AuditEvent[]> {
  const r = unwrap(await api.GET('/api/projects/{public_id}/audit', { params: { path: { public_id: projectId } } }));
  return r.results;
}

/** The project as a reference file (P14); the filename comes from the server. */
export async function exportProject(id: string): Promise<{ blob: Blob; filename: string }> {
  const { data, response, error } = await api.GET('/api/projects/{public_id}/export', {
    params: { path: { public_id: id } },
    parseAs: 'blob',
  });
  if (error !== undefined || !response.ok || !data) {
    const e = error as { error?: { code?: string; message?: string } } | undefined;
    throw new ApiError(response.status, e?.error?.code ?? 'http_error', e?.error?.message ?? response.statusText);
  }
  const match = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '');
  return { blob: data as Blob, filename: match?.[1] ?? `forestum-project-${id}.json` };
}

/** Creates a project in the organization from a reference file; the body is the parsed export document. */
export async function importProject(orgId: string, payload: unknown): Promise<Project> {
  return unwrap(await api.POST('/api/organizations/{public_id}/import-project', { params: { path: { public_id: orgId } }, body: payload as never }));
}

export function useProjects(f: ProjectFilters = {}) {
  return useQuery({ queryKey: ['projects', f], queryFn: () => listProjects(f) });
}
export function useProject(id: string | undefined) {
  return useQuery({ queryKey: ['project', id], queryFn: () => getProject(id!), enabled: !!id });
}
export function useShares(projectId: string | undefined, enabled = true) {
  return useQuery({ queryKey: ['shares', projectId], queryFn: () => listShares(projectId!), enabled: !!projectId && enabled });
}
export function useProjectAudit(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['project-audit', projectId],
    queryFn: () => listProjectAudit(projectId!),
    enabled: !!projectId && enabled,
  });
}

function useInvalidateProject(id?: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['projects'] });
    if (id) void qc.invalidateQueries({ queryKey: ['project', id] });
    if (id) void qc.invalidateQueries({ queryKey: ['project-audit', id] });
  };
}

export function useImportProject(orgId: string) {
  const invalidate = useInvalidateProject();
  return useMutation({ mutationFn: (payload: unknown) => importProject(orgId, payload), onSuccess: invalidate });
}
export function useCreateProject() {
  const invalidate = useInvalidateProject();
  return useMutation({ mutationFn: createProject, onSuccess: invalidate });
}
export function useUpdateProject(id: string) {
  const invalidate = useInvalidateProject(id);
  return useMutation({ mutationFn: (patch: ProjectPatch) => updateProject(id, patch), onSuccess: invalidate });
}
export function useLifecycle(id: string) {
  const invalidate = useInvalidateProject(id);
  return useMutation({
    mutationFn: ({ action, reason }: { action: LifecycleAction; reason?: string }) => lifecycle(id, action, reason),
    onSuccess: invalidate,
  });
}
export function useGrantShare(projectId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: (body: GrantShareBody) => grantShare(projectId, body),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['shares', projectId] });
    },
  });
}
export function useRevokeShare(projectId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: (shareId: string) => revokeShare(projectId, shareId),
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['shares', projectId] });
    },
  });
}
